import React from 'react';
import BannerBlock from './BannerBlock';
import TextBlock from './TextBlock';
import FeatureGridBlock from './FeatureGridBlock';
import ProductListBlock from './ProductListBlock';
import ModularGridBlock from './ModularGridBlock';
import GlobalBlockRenderer from './GlobalBlockRenderer';
import ThematicPageCardBlock from './ThematicPageCardBlock';
import CarouselBannerBlock from './CarouselBannerBlock';
import PromotionCardsGridBlock from './PromotionCardsGridBlock';
import PartnerCardsGridBlock from './PartnerCardsGridBlock';
import NavigationCardsBlock from './NavigationCardsBlock';
import BlogPostsBlock from './BlogPostsBlock';
import VitrinBlock from './VitrinBlock';

/**
 * Universal Block Renderer V7
 * 
 * Component map is defined here alongside imports.
 * The CMS_BLOCK_REGISTRY (cmsBlockRegistry.js) is the source of truth
 * for WHICH blocks are allowed WHERE. This file maps type → React component.
 * 
 * ADDING A NEW BLOCK:
 * 1. Import component
 * 2. Add to blockComponents
 * 3. Add entry to cmsBlockRegistry.js
 */

// Component map: block type → React component
const blockComponents = {
  banner: BannerBlock,
  banner_fixed_centered: BannerBlock,
  carousel_banner: CarouselBannerBlock,
  promotion_cards_grid: PromotionCardsGridBlock,
  partner_cards_grid: PartnerCardsGridBlock,
  navigation_cards: NavigationCardsBlock,
  modular_grid: ModularGridBlock,
  text: TextBlock,
  feature_grid: FeatureGridBlock,
  product_list: ProductListBlock,
  thematic_page_card: ThematicPageCardBlock,
  blog_posts: BlogPostsBlock,
  vitrin_block: VitrinBlock,
};
const BlockRenderer = ({ block }) => {
  if (!block || !block.is_active) {
    return null;
  }

  // ========== GESTION DES BLOCS GLOBAUX ==========
  // Si le bloc est une référence à un bloc global, utiliser GlobalBlockRenderer
  if (block.type === 'global_block_reference') {
    const { global_block_id, global_block_type, global_block_title } = block.content || {};
    
    if (!global_block_id) {
      console.error('Bloc global_block_reference sans global_block_id:', block);
      return null;
    }

    return (
      <GlobalBlockRenderer
        globalBlockId={global_block_id}
        globalBlockType={global_block_type}
        globalBlockTitle={global_block_title}
      />
    );
  }

  // ========== GESTION DES BLOCS CLASSIQUES ==========
  const BlockComponent = blockComponents[block.type];

  if (!BlockComponent) {
    console.warn(`Unknown block type: ${block.type}`);
    return null;
  }

  // Appliquer le padding externe si défini
  const externalPadding = block.content?.external_padding || {};
  const paddingStyle = {
    paddingTop: externalPadding.top ? `${externalPadding.top}px` : '0px',
    paddingRight: externalPadding.right ? `${externalPadding.right}px` : '0px',
    paddingBottom: externalPadding.bottom ? `${externalPadding.bottom}px` : '0px',
    paddingLeft: externalPadding.left ? `${externalPadding.left}px` : '0px',
  };

  // Surface variants (CMS presets pour profondeur visuelle)
  const bgVariant = block.content?.surface_bg || 'flat';
  const borderVariant = block.content?.surface_border || 'none';
  const shadowVariant = block.content?.surface_shadow || 'none';
  const hasSurface = bgVariant !== 'flat' || borderVariant !== 'none' || shadowVariant !== 'none';

  // Background variant styles
  const surfaceStyle = {};

  if (bgVariant === 'soft-gradient') {
    surfaceStyle.background = 'linear-gradient(180deg, rgba(247,245,242,0.4) 0%, rgba(255,255,255,0) 100%)';
  } else if (bgVariant === 'radial-soft') {
    surfaceStyle.background = 'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.6) 0%, rgba(247,245,242,0) 70%)';
  } else if (bgVariant === 'subtle-pattern') {
    surfaceStyle.backgroundImage = `url("data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='0.6' fill='rgba(0,0,0,0.03)'/%3E%3C/svg%3E")`;
    surfaceStyle.backgroundSize = '20px 20px';
  }

  if (borderVariant === 'subtle') {
    surfaceStyle.border = '1px solid rgba(0,0,0,0.06)';
    surfaceStyle.borderRadius = '12px';
  } else if (borderVariant === 'accent') {
    surfaceStyle.border = '1px solid rgba(180,160,130,0.15)';
    surfaceStyle.borderRadius = '12px';
  }

  if (shadowVariant === 'soft') {
    surfaceStyle.boxShadow = '0 1px 2px rgba(140,120,90,0.04), 0 4px 12px rgba(140,120,90,0.06)';
  } else if (shadowVariant === 'elevated') {
    surfaceStyle.boxShadow = '0 2px 4px rgba(140,120,90,0.04), 0 8px 24px rgba(140,120,90,0.08)';
  }

  // Quand les surface variants sont actives, ajouter un padding minimum
  // pour que le fond/border/shadow soient visibles autour du composant enfant
  if (hasSurface) {
    surfaceStyle.padding = `${Math.max(externalPadding.top || 12, 12)}px ${Math.max(externalPadding.right || 12, 12)}px ${Math.max(externalPadding.bottom || 12, 12)}px ${Math.max(externalPadding.left || 12, 12)}px`;
    surfaceStyle.overflow = 'visible';
    if (!surfaceStyle.borderRadius) {
      surfaceStyle.borderRadius = '12px';
    }
  }

  const combinedStyle = hasSurface
    ? surfaceStyle
    : { ...paddingStyle };

  return (
    <div 
      data-block-id={block.id} 
      data-block-type={block.type}
      style={combinedStyle}
    >
      <BlockComponent block={block} />
    </div>
  );
};

export default BlockRenderer;